import { useState, useEffect, useCallback, useRef } from 'react';
import { checkUserMessageLimit, RateLimitResult, ChatLimitMode } from '@/services/rateLimitService';
import { sendLimitReachedEmail } from '@/services/emailService';
import { supabase } from '@/integrations/supabase/client';

interface UseMessageLimitResult {
  limitInfo: RateLimitResult | null;
  isLoading: boolean;
  isLimitReached: boolean;
  remaining: number;
  refresh: () => Promise<void>;
}

export function useMessageLimit(
  userId: string | undefined,
  plan: string,
  mode: ChatLimitMode = 'funcionarios'
): UseMessageLimitResult {
  const [limitInfo, setLimitInfo] = useState<RateLimitResult | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const emailSentRef = useRef(false);

  const checkLimit = useCallback(async () => {
    if (!userId) {
      setIsLoading(false);
      return;
    }

    try {
      const result = await checkUserMessageLimit(userId, plan, mode);
      setLimitInfo(result);

      // Send email when limit is reached for the first time
      if (result.isLimitReached && !emailSentRef.current) {
        const storageKey = `limit_email_sent_${userId}_${mode}`;
        const alreadySent = localStorage.getItem(storageKey);
        
        if (!alreadySent) {
          // Get user email from profile
          const { data: profile } = await supabase
            .from('profiles')
            .select('email, full_name')
            .eq('id', userId)
            .single();

          if (profile?.email) {
            await sendLimitReachedEmail(profile.email, profile.full_name || undefined);
            localStorage.setItem(storageKey, 'true');
          }
          emailSentRef.current = true;
        }
      }
    } catch (error) {
      console.error('Error checking message limit:', error);
    } finally {
      setIsLoading(false);
    }
  }, [userId, plan, mode]);

  useEffect(() => {
    checkLimit();
  }, [checkLimit]);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    await checkLimit();
  }, [checkLimit]);

  return {
    limitInfo,
    isLoading,
    isLimitReached: limitInfo?.isLimitReached ?? false,
    remaining: limitInfo?.remaining ?? 20,
    refresh,
  };
}
